/**
 * The small client-side bridge between a chat turn and the canvas artifact it caused.
 *
 * The server owns the message and the canvas owns the artifact. This record only joins the two
 * while the server's history changes a temporary optimistic message id into its canonical id.
 */
import type { PlanningChatMessage } from '~/types/planning';
import { stripSystemContext } from '~/utils/planning-utils';
import type { ArtifactWorkNote } from '~/design-system/components/DeckGen/birtvyOpen/artifactNarration';

export interface ArtifactOutcome {
  slideId: number;
  operation: 'edit' | 'generate';
  status: 'complete' | 'failed';
  title?: string;
  summary?: string;
  notes?: ArtifactWorkNote[];
  finishedAt: number;
}

export interface ArtifactReceipt {
  /** The id used by the stream when the receipt was first observed. */
  messageId: string;
  /** The slide ids created or updated by that turn. */
  slideIds: number[];
  /** The user's visible prompt, used to reattach after a history refresh. */
  prompt?: string;
  /** The local time at which the turn started. */
  startedAt: number;
  chatFinishedAt?: number;
  dispatched?: boolean;
  /** A reload cannot resume a browser-owned generation that never reported a saved result. */
  interrupted?: boolean;
  outcomes?: ArtifactOutcome[];
}

export function receiptStartedAt(messageId: string, startedAt?: number): number {
  if (Number.isFinite(startedAt)) return startedAt as number;
  const timestamp = /^assistant-(\d+)$/.exec(messageId)?.[1];
  return timestamp ? Number(timestamp) : 0;
}

export function cleanSlideIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((id): id is number => typeof id === 'number' && Number.isFinite(id)))];
}

const normalizePrompt = (value?: string) => stripSystemContext(value ?? '').replace(/\s+/g, ' ').trim();

/** Assign each receipt once. Repeating a prompt must not attach the new result to an older failure. */
export function resolveArtifactReceipts(
  messages: PlanningChatMessage[],
  receipts: ArtifactReceipt[],
): Map<string, ArtifactReceipt[]> {
  const owners: { messageId: string; prompt: string; at: number }[] = [];
  let user: PlanningChatMessage | undefined;
  for (const message of messages) {
    if (message.role === 'user') { user = message; continue; }
    if (message.role !== 'assistant') continue;
    owners.push({ messageId: message.id, prompt: normalizePrompt(user?.content), at: new Date(user?.createdAt ?? message.createdAt).getTime() });
  }
  const result = new Map<string, ArtifactReceipt[]>();
  const assigned = new Set<ArtifactReceipt>();
  const attach = (messageId: string, receipt: ArtifactReceipt) => {
    result.set(messageId, [...(result.get(messageId) ?? []), receipt]);
    assigned.add(receipt);
  };
  for (const receipt of receipts) {
    if (owners.some((owner) => owner.messageId === receipt.messageId)) attach(receipt.messageId, receipt);
  }
  const candidates = receipts.filter((receipt) => !assigned.has(receipt)).flatMap((receipt) =>
    owners.filter((owner) => !result.has(owner.messageId) && (!receipt.prompt || owner.prompt === normalizePrompt(receipt.prompt)))
      .map((owner) => ({ receipt, owner, distance: Math.abs(receipt.startedAt - owner.at) }))
      .filter(({ distance }) => distance <= 15 * 60 * 1000),
  ).sort((a, b) => a.distance - b.distance);
  for (const { receipt, owner } of candidates) {
    if (!assigned.has(receipt) && !result.has(owner.messageId)) attach(owner.messageId, receipt);
  }
  return result;
}

/** Only confirmed host results can replace an anticipatory chat response with a completion. */
export function artifactCompletionText(outcomes: ArtifactOutcome[]): string {
  if (outcomes.length > 3) {
    const complete = outcomes.filter(outcome => outcome.status === 'complete');
    const failed = outcomes.length - complete.length;
    return `${complete.length} artifacts saved${failed ? `; ${failed} could not be completed` : ''}. ${failed ? 'The completed artifacts are available in the directory; retry the failed items.' : 'The deck is ready to review in the directory.'}`;
  }
  return outcomes.map((outcome) => {
    const name = outcome.title ? `“${outcome.title}”` : `artifact ${String(outcome.slideId).padStart(2, '0')}`;
    if (outcome.status === 'failed') return `Could not ${outcome.operation === 'edit' ? 'update' : 'create'} ${name}. The artifact was not saved.`;
    // No request-to-past-tense rewrite: intention isn't evidence that a change happened.
    if (outcome.summary?.trim()) return outcome.summary.trim();
    return `${outcome.operation === 'edit' ? 'Updated' : 'Created'} ${name}.`;
  }).join('\n\n');
}

/** Wall time for the whole turn, including canvas work after the model response ended. */
export function receiptDurationSec(receipts: ArtifactReceipt[]): number | undefined {
  const starts = receipts.map((r) => r.startedAt).filter((at) => Number.isFinite(at) && at > 0);
  const ends = receipts.flatMap((r) => [r.chatFinishedAt ?? 0, ...(r.outcomes ?? []).map((o) => o.finishedAt)]).filter((at) => at > 0);
  if (!starts.length || !ends.length) return undefined;
  return Math.max(0, Math.round((Math.max(...ends) - Math.min(...starts)) / 1000));
}
